import { TextField, Box, Typography, InputLabel } from "@mui/material";
import { useState, useRef, useEffect } from "react";

const plateCharacters = (value) => Array.from(String(value || '').toUpperCase())
  .slice(0, 6)
  .concat(Array(6).fill(''))
  .slice(0, 6);

const PlacaTrailerInput = ({ onComplete, error, disabled, validationPlaca, defaultValue = '', resetKey }) => {
  const [plate, setPlate] = useState(() => plateCharacters(defaultValue));
  const inputsRef = useRef([]);

  const placeholders = ["A", "1", "2", "3", "4", "5"]; // Placeholders individuales

  useEffect(() => {
    setPlate(plateCharacters(defaultValue));
  }, [defaultValue, resetKey]);

  const handleChange = (index, event) => {
    if (validationPlaca !== undefined) {
      validationPlaca();
    }
    let value = event.target.value.toUpperCase();

    if (index === 0) {
      value = value.replace(/[^A-Z]/g, ""); // Solo letras en la primera posición
    } else {
      value = value.replace(/[^0-9]/g, ""); // Solo números en las siguientes posiciones
    }

    const newPlate = [...plate];
    newPlate[index] = value;
    setPlate(newPlate);

    // Auto-focus al siguiente input si el valor es válido
    if (index < 9 && value !== "") {
      inputsRef.current[index + 1]?.focus();
    }

    // Llamar a onComplete cuando todos los campos estén llenos
    if (newPlate.slice(0, 6).every((char) => char !== "") && newPlate.slice(6).every((char) => char !== "")) {
      onComplete(newPlate.join(""));
    }
    if (validationPlaca !== undefined) {
      validationPlaca(newPlate.join(""));
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !plate[index] && index > 0) {
      // Si el campo está vacío y el usuario presiona Backspace, mover al anterior
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index) => {
    if (plate[index] === "") {
      inputsRef.current[index]?.focus();
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} marginBottom={2}>
        {/* Letra inicial */}
        <InputLabel sx={{ fontWeight: 'bold' }}>PLACA:</InputLabel>
        <Box display="flex" gap={1} sx={{ marginLeft: 1 }}>
          <TextField
            disabled={disabled}
            key={0}
            inputRef={(el) => (inputsRef.current[0] = el)}
            value={plate[0]}
            onChange={(e) => handleChange(0, e)}
            onKeyDown={(e) => handleKeyDown(0, e)}
            onFocus={() => handleFocus(0)}
            variant="outlined"
            size="small"
            placeholder={placeholders[0]} // Placeholder dinámico
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: "center",
                textTransform: "uppercase",
                width: "2.5ch",
              },
            }}
            error={error}
          />
        </Box>

        {/* Separador en el centro */}
        <Typography variant="h5">-</Typography>

        {/* Números */}
        <Box display="flex" gap={1}>
          {plate.slice(1, 6).map((char, index) => (
            <TextField
              disabled={disabled}
              key={index + 1}
              inputRef={(el) => (inputsRef.current[index + 1] = el)}
              value={char}
              onChange={(e) => handleChange(index + 1, e)}
              onKeyDown={(e) => handleKeyDown(index + 1, e)}
              onFocus={() => handleFocus(index + 1)}
              variant={disabled ? "filled" : "outlined"}
              size="small"
              placeholder={placeholders[index + 1]} // Placeholder dinámico
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  width: "2.5ch",
                },
              }}
              error={error}
            />
          ))}
        </Box>

        {/* Letra final */}
        <Box display="flex" gap={1} sx={{ marginLeft: 1 }}>
          {plate.slice(6, 10).map((char, index) => (
            <TextField
              key={index + 6}
              inputRef={(el) => (inputsRef.current[index + 6] = el)}
              value={char}
              onChange={(e) => handleChange(index + 6, e)}
              onKeyDown={(e) => handleKeyDown(index + 6, e)}
              onFocus={() => handleFocus(index + 6)}
              variant={disabled ? "filled" : "outlined"}
              size="small"
              placeholder={placeholders[index + 6]} // Placeholder dinámico
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  width: "2.5ch",
                },
              }}
              error={error}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

export default PlacaTrailerInput;
